import { INTEREST } from "@townsy/contracts";

/**
 * ADR-0043 decision 1 (nearby-only syncing): in Overview the street room must also send what the camera looks at, so
 * the tab tells it its camera's ground focus (`view.focus`). Pure logic here (the session polls the scene and sends):
 *  - the radius the overview shows, from the camera's distance to its target;
 *  - a throttle: at most `INTEREST.focusSendHz` messages a second, only when the focus moved or zoomed noticeably,
 *    a trailing send once it settles (the next poll after the interval), and one `null` when Overview ends.
 */

export interface FocusPoint {
  x: number;
  z: number;
  radius: number;
}

/**
 * The ground radius the Overview camera shows around its target: 1.5 x its distance from the target (the view reaches
 * about 1.5 radii beyond the target at the usual tilt and 0.75 radii to each side), at least 150 m (a district framing,
 * radius about 100, then covers its district's width; the tilted view also shows the street beyond, where people are a
 * few pixels tall) and at most the server's 400 m (the 280 m zoom limit, where a person is about 5 px tall).
 */
export const OVERVIEW_FOCUS_FACTOR = 1.5;
export const OVERVIEW_FOCUS_FLOOR_METRES = 150;
export function overviewFocusRadius(cameraRadius: number): number {
  const r = Number.isFinite(cameraRadius) ? cameraRadius * OVERVIEW_FOCUS_FACTOR : OVERVIEW_FOCUS_FLOOR_METRES;
  return Math.min(INTEREST.overviewRadiusMaxMetres, Math.max(OVERVIEW_FOCUS_FLOOR_METRES, INTEREST.overviewRadiusMinMetres, Math.round(r)));
}

/** A pan this small or a zoom within this fraction does not need a new message (the server's hysteresis is 30 m). */
const MOVE_EPSILON_METRES = 8;
const ZOOM_EPSILON_FRACTION = 0.08;

const UNSENT = Symbol("unsent");

export class ViewFocusSender {
  /** What the server was last told: a focus, null (no Overview focus), or nothing yet in this membership. */
  private last: FocusPoint | null | typeof UNSENT = UNSENT;
  private lastAt = Number.NEGATIVE_INFINITY;
  /** Messages sent (diagnostics, the test hook). */
  sent = 0;

  constructor(
    private readonly send: (focus: FocusPoint | null) => boolean,
    private readonly minIntervalMs = 1000 / INTEREST.focusSendHz,
  ) {}

  /** The focus the server was last told (null: none; undefined: nothing sent in this membership). */
  get lastSent(): FocusPoint | null | undefined {
    return this.last === UNSENT ? undefined : this.last;
  }

  /** A new room membership: the server knows nothing yet. */
  reset(): void {
    this.last = UNSENT;
    this.lastAt = Number.NEGATIVE_INFINITY;
  }

  /** The current focus (null when not in Overview on the street), every poll. */
  poll(focus: FocusPoint | null, now: number): void {
    if (focus === null) {
      if (this.last === UNSENT) {
        this.last = null; // the server starts without an Overview focus: nothing to clear
        return;
      }
      if (this.last === null) return;
      if (this.send(null)) this.mark(null, now);
      return;
    }
    if (!Number.isFinite(focus.x) || !Number.isFinite(focus.z) || !Number.isFinite(focus.radius)) return;
    if (this.last !== UNSENT && this.last !== null && !changed(this.last, focus)) return;
    if (now - this.lastAt < this.minIntervalMs) return; // a later poll sends it (trailing)
    if (this.send(focus)) this.mark({ ...focus }, now);
  }

  private mark(focus: FocusPoint | null, now: number): void {
    this.last = focus;
    this.lastAt = now;
    this.sent++;
  }
}

function changed(a: FocusPoint, b: FocusPoint): boolean {
  if (Math.hypot(a.x - b.x, a.z - b.z) > MOVE_EPSILON_METRES) return true;
  return Math.abs(a.radius - b.radius) > Math.max(1, a.radius * ZOOM_EPSILON_FRACTION);
}
